import httpClient from './httpClient';
import { API_ENDPOINTS } from '../../constants/endpoints';

const createRequestWithFallback = (endpoint) => {
  return async () => {
    try {
      const response = await httpClient.get(endpoint);
      return response;
    } catch (error) {
      throw error;
    }
  };
};

const parseResponse = (response) => {
  // Si la respuesta tiene un body como string, parsearlo
  let data = response.data;
  
  if (data && typeof data.body === 'string') {
    try {
      data = JSON.parse(data.body);
    } catch (e) {
      // Si falla el parseo, mantener data original
    }
  }
  
  // Si execution-result es un string JSON, parsearlo también
  if (data && data['execution-result'] && typeof data['execution-result'] === 'string') {
    try {
      // Verificar si contiene un error de AWS (NoSuchKey, etc)
      if (data['execution-result'].includes('NoSuchKey') || 
          data['execution-result'].includes('does not exist')) {
        // Lanzar error para que sea capturado por el catch del polling
        const error = new Error(data['execution-result']);
        error.isS3Error = true;
        throw error;
      }
      data['execution-result'] = JSON.parse(data['execution-result']);
    } catch (e) {
      // Si es un error de S3, re-lanzarlo
      if (e.isS3Error) {
        throw e;
      }
      // Si falla el parseo por otra razón, mantener como string
    }
  }
  
  return data;
};

const getScriptName = (scriptType) => {
  const scripts = {
    create: import.meta.env.VITE_PS_SCRIPT_CREATE_USER || 'pca.add-ad-users.ps1',
    edit: import.meta.env.VITE_PS_SCRIPT_EDIT_USER || 'pca.update-ad-users.ps1',
    disable: import.meta.env.VITE_PS_SCRIPT_DISABLE_USER || 'pca.disable-ad-users.ps1',
    enable: import.meta.env.VITE_PS_SCRIPT_ENABLE_USER || 'pca.enable-ad-users.ps1',
    changePassword: import.meta.env.VITE_PS_SCRIPT_CHANGE_PASSWORD || 'pca.change-ad-users-passwords.ps1',
    sendVerificationCode: import.meta.env.VITE_PS_SCRIPT_SEND_CODE || 'pca.self-service-send-code.ps1'
  };
  return scripts[scriptType];
};

export const awsService = {
  createAccount: (accountData) => httpClient.post(API_ENDPOINTS.AWS.CREATE_ACCOUNT, accountData),
  
  createADUsers: async (usersData) => {
    const response = await httpClient.post(
      `${API_ENDPOINTS.AWS.RUN_POWERSHELL_SCRIPT}?ps-script-name=${getScriptName('create')}`,
      usersData
    );
    return parseResponse(response);
  },
  
  editADUsers: async (usersData) => {
    const response = await httpClient.post(
      `${API_ENDPOINTS.AWS.RUN_POWERSHELL_SCRIPT}?ps-script-name=${getScriptName('edit')}`,
      usersData
    );
    return parseResponse(response);
  },
  
  disableADUsers: async (usersData) => {
    const response = await httpClient.post(
      `${API_ENDPOINTS.AWS.RUN_POWERSHELL_SCRIPT}?ps-script-name=${getScriptName('disable')}`,
      usersData
    );
    return parseResponse(response);
  },

  enableADUsers: async (usersData) => {
    const response = await httpClient.post(
      `${API_ENDPOINTS.AWS.RUN_POWERSHELL_SCRIPT}?ps-script-name=${getScriptName('enable')}`,
      usersData
    );
    return parseResponse(response);
  },
  
  changeUserPassword: async (usersData) => {
    const response = await httpClient.post(
      `${API_ENDPOINTS.AWS.RUN_POWERSHELL_SCRIPT}?ps-script-name=${getScriptName('changePassword')}`,
      usersData
    );
    return parseResponse(response);
  },
  
  getExecutionResult: async (executionId) => {
    const response = await httpClient.get(
      `${API_ENDPOINTS.AWS.EXECUTION_RESULTS}?execution-id=${executionId}`
    );
    return parseResponse(response);
  },
  
  searchADUsers: async (searchText) => {
    const response = await httpClient.get(
      `${API_ENDPOINTS.AWS.SEARCH_AD_USERS}?text-to-search=${encodeURIComponent(searchText)}`
    );
    return response.data;
  },

  updatePermissionSetAssignments: async (body) => {
    const functionName = import.meta.env.VITE_LAMBDA_FUNCTION_UPDATE_ASSIGNMENTS || 'cauto-update-ps-assignments';
    const response = await httpClient.post(
      `${API_ENDPOINTS.AWS.RUN_METHOD}?function-name=${functionName}&return-results=False`,
      body
    );
    return response.data;
  },

  sendVerificationCode: async ({ UserEmail, VerificationCode, MinutesValid }) => {
    const response = await httpClient.post(
      `${API_ENDPOINTS.AWS.RUN_POWERSHELL_SCRIPT}?ps-script-name=${getScriptName('sendVerificationCode')}`,
      { UserEmail, VerificationCode, MinutesValid }
    );
    return parseResponse(response);
  },
  
  getReportsAwsS3: createRequestWithFallback(API_ENDPOINTS.AWS.S3),
  getReportsAwsEC2: createRequestWithFallback(API_ENDPOINTS.AWS.EC2),
  getReportsAwsIamUsers: createRequestWithFallback(API_ENDPOINTS.AWS.IAM_USERS),
  getReportsAwsADUsers: createRequestWithFallback(API_ENDPOINTS.AWS.AD_USERS),
  getReportsAwsADGroups: createRequestWithFallback(API_ENDPOINTS.AWS.AD_GROUPS),
  getReportsAwsRDS: createRequestWithFallback(API_ENDPOINTS.AWS.RDS),
  getReportsAwsEFS: createRequestWithFallback(API_ENDPOINTS.AWS.EFS),
  getReportsAwsAccounts: createRequestWithFallback(API_ENDPOINTS.AWS.ACCOUNTS),
  getReportsAwsMonthlyCosts: createRequestWithFallback(API_ENDPOINTS.AWS.MONTHLY_COSTS),
  getCostCenter: createRequestWithFallback(API_ENDPOINTS.ACCOUNTS.COST_CENTER),
  getQuickSightEmbedUrl: (dashboard_id) => httpClient.get(`${API_ENDPOINTS.AWS.QUICKSIGHT}?dashboard_id=${dashboard_id}`),
};
